import { Shield, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ProofShieldProps {
  /** Visual size. xs = inline-with-text (12px), sm = card overlay (16px), md = item header (20px) */
  size?: 'xs' | 'sm' | 'md';
  /** Render with a circular dark backdrop — use when overlaying images. */
  backdrop?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * Compact gold-shield trust signal for items with a verified Proof Pack
 * (possession photo + per-category proof set). Render only when
 * `item.has_proof_pack === true`. For the full pill version use
 * `<ProofPackBadge />` on the item detail page.
 */
export function ProofShield({
  size = 'sm',
  backdrop = false,
  className,
  ariaLabel = 'Proof Pack verified',
}: ProofShieldProps) {
  const iconSize = size === 'xs' ? 'h-3 w-3' : size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const checkSize = size === 'xs' ? 'h-2 w-2' : size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5';
  const boxSize = size === 'xs' ? 'h-4 w-4' : size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';

  const inner = (
    <span
      className={cn(
        'relative inline-flex items-center justify-center',
        backdrop && cn(
          'rounded-full bg-black/60 backdrop-blur-sm ring-1 ring-[hsl(var(--active-gold))]/40',
          boxSize,
        ),
        className,
      )}
      aria-label={ariaLabel}
    >
      <Shield
        className={cn(iconSize, 'text-[hsl(var(--active-gold))]')}
        strokeWidth={2.25}
      />
      <Check
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-success drop-shadow-[0_0_3px_hsl(var(--success)/0.7)]',
          checkSize,
        )}
        strokeWidth={3}
      />
    </span>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px]">
          <p className="text-xs">
            Proof Pack verified — seller uploaded a possession photo with their handle and today's date.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ProofShield;
